/**
  * @prop --input-field-width: Sets the width of the input field
  * @prop --input-field-max-width: Sets the max width of the input field
  * @prop --input-field-outline: Sets the outline of the input field
  * @prop --input-field-border-width: Sets the width of the input field
  * @prop --input-field-border-radius: Sets the border radius of the input field
  * @prop --input-field-border-color: Sets the border color of the input field
  * @prop --input-field-border-color-hover: Sets the border color on hover
  * @prop --input-field-border-color-focus: Sets the border color on focus
  * @prop --input-field-border-color-warning: Sets the border color for warning states
  * @prop --input-field-border-color-error: Sets the border color for error states
  * @prop --input-field-border-color-readonly: Sets the border color for a readonly input field
  * @prop --input-field-border-color-disabled: Sets the border color for a disabled input field
  * @prop --input-field-background-color: Sets the background color for input field
  */

:host {
  --input-field-max-width: 328px;
  --input-field-width: 100%;
  --input-field-outline: var(--s-color-outline-default);
  --input-field-border-width: var(--s-size-border-width-default);
  --input-field-border-bottom-width: var(--s-size-border-width-default);
  --input-field-border-top-left-radius: var(--s-size-radius-medium);
  --input-field-border-top-right-radius: var(--s-size-radius-medium);
  --input-field-border-bottom-right-radius: var(--s-size-radius-medium);
  --input-field-border-bottom-left-radius: var(--s-size-radius-medium);
  --input-field-border-color: var(--s-color-border-default);
  --input-field-border-color-hover: var(--s-color-outline-default);
  --input-field-border-color-focus: var(--s-color-outline-default);
  --input-field-border-color-warning: var(--s-color-border-error);
  --input-field-border-color-error: var(--s-color-border-error);
  --input-field-border-color-readonly: var(--s-color-border-divider);
  --input-field-border-color-disabled: var(--b-color-neutral-lighter);
  --input-field-text-align: left;
  --input-field-padding-top: var(--p-size-static-150);
  --input-field-padding-right: var(--p-size-static-250);
  --input-field-padding-bottom: var(--p-size-static-150);
  --input-field-padding-left: var(--p-size-static-200);
  --input-field-background-color: var(--b-color-white);

  max-width: var(--input-field-max-width);
}

@mixin msg-elm {
  display: block;
  margin-top: calc(var(--p-size-static-100) * -1);
  color: var(--s-color-text-default);
  text-align: left;
  font-weight: 300;
  line-height: var(--p-size-dynamic-300);
}

@mixin error-msg-elm {
  display: flex;
  gap: var(--p-size-static-200);
  justify-content: space-between;
  margin: 0 0 var(--p-size-static-100);
  background-color: var(--b-color-error-light);
  padding: var(--p-size-static-100) var(--p-size-static-200);
  border-radius: var(--s-size-radius-medium);
  line-height: var(--p-size-dynamic-300);
}

.anchor-c-input__container {
  display: flex;
  flex-direction: column;
  gap: var(--p-size-static-100);
  &.error {
    border-left: 4px var(--s-color-border-error) solid;
    padding-left: 16px;
  }
}

.anchor-c-input {
  $input: &; // Storing 'anchor-c-input' as variable called $input to be used as a shorthand
  // The Input element does not use a shadow root and therefore we cannot use the :host pseudo selector
  // instead we use the :root, and in turn we need to use more targeted names for the variables
  // Default variables

  --input-line-height: var(--p-size-dynamic-300);
  --input-width: calc(var(--input-field-width)); // - var(--input-border-width) * 2);

  // Expanded variables
  --input-padding-top: var(--input-field-padding-top);
  --input-padding-right: var(--input-field-padding-right);
  --input-padding-bottom: var(--input-field-padding-bottom);
  --input-padding-left: var(--input-field-padding-left);

  --input-border-width: var(--input-field-border-width);
  --border-radius: var(--input-field-border-top-left-radius) var(--input-field-border-top-right-radius) var(--input-field-border-bottom-right-radius)
    var(--input-field-border-bottom-left-radius);
  --border-color: var(--input-field-border-color);
  --border-color-hover: var(--input-field-border-color-hover);
  --border-color-focus: var(--input-field-border-color-focus);
  --border-color-warning: var(--input-field-border-color-warning);
  --border-color-error: var(--input-field-border-color-error);
  --border-color-readonly: var(--input-field-border-color-readonly);
  --border-color-disabled: var(--input-field-border-color-disabled);
  width: var(--input-width);
  border-radius: var(--border-radius);
  border-width: var(--input-border-width);
  border-bottom-width: var(--input-field-border-bottom-width);
  border-style: solid;
  border-color: var(--border-color);
  display: flex;
  &:hover {
    --border-color: var(--input-field-border-color-hover);
    outline: var(--s-color-outline-aura-hover) solid var(--s-size-outline-aura-width);
  }
  &:focus-visible,
  &:focus,
  &:focus-within {
    --border-color: var(--border-color-focus);
    outline: var(--input-field-outline) solid var(--s-size-outline-focus-ring-width);
    outline-offset: var(--s-size-outline-focus-ring-offset);
  }
  &--no-spin-button {
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type=number] {
      -moz-appearance: textfield;
      appearance: textfield;
    }
  }

  input {
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
    line-height: var(--input-line-height);
    font-size: var(--s-size-text-default);
    text-align: var(--input-field-text-align, 'left');
    color: var(--s-color-text-default);
    background-color: var(--input-field-background-color);
    cursor: text;
    padding-top: calc(var(--input-padding-top) - var(--input-border-width));
    padding-right: calc(var(--input-padding-right) - var(--input-border-width));
    padding-bottom: calc(var(--input-padding-bottom) - var(--input-border-width));
    padding-left: calc(var(--input-padding-left) - var(--input-border-width));
    outline: none;
    border: none;

    &[readonly] {
      --border-color: var(--border-color-readonly);
      --input-padding-left: 0px;
      --input-padding-right: 0px;
      --input-padding-bottom: 0px;
      --input-padding-top: 0px;
      --input-field-border-width: 0px;
      --input-field-border-bottom-width: 0px;
      font-weight: 600;
      background-color: transparent;
      outline: none;
    }
    &[disabled] {
      --border-color: var(--input-field-border-color-disabled);
      background-color: var(--b-color-neutral-lighter);
      color: var(--s-color-text-disabled);
      outline: none;
      &:hover {
        cursor: not-allowed;
      }
    }
  }

  &--warning {
    --border-color: var(--border-color-warning);
  }
  &--error {
    --border-color: var(--input-field-border-color-error);
  }
  &:has(input::placeholder) {
    color: var(--b-color-neutral-dark);
  }
  &__help-msg {
    @include msg-elm();
  }
  &__warning-msg {
    @include msg-elm();
    font-style: italic;
  }
  &__error-msg {
    @include error-msg-elm();
  }
  &__heading {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;

    & > .anchor-c-label {
      flex-grow: 1;
    }
  }
  &__label {
    color: var(--s-color-text-default);
    order: 1;
    display: flex;
    justify-content: space-between;
    line-height: var(--p-size-dynamic-300);
    &-text {
      flex-grow: 1;
    }
    &-suffix {
      display: inline-flex;
    }
  }

  &__prefix {
    display: inline-flex;
    order: 0;
  }

  &__messages {
    &--inside {
      order: 2;
    }
    &--below {
      order: 4;
      #{$input}__help-msg {
        margin-top: 0;
      }
      #{$input}__error-msg {
        margin: var(--p-size-static-100) 0 0;
      }
    }
  }
  &:has(input[readonly='']) {
    --border-color: var(--border-color-readonly);
    --input-field-border-width: 0px;
    --input-field-border-bottom-width: 0px;
    background-color: transparent;
    outline: none;
  }

  &--with-icon-inside {
    background-color: var(--input-field-background-color);
    padding-right: calc(52px - var(--input-field-border-width));
    &:focus-within {
      padding-right: calc(52px - 2px);
    }
  }
  &--with-icon-outside {
    margin-right: calc(52px + var(--input-field-border-width));
  }
  &--set-width {
    --input-width: unset;
  }
  &__icon-inside,
  &__icon-outside {
    position: absolute;
    right: var(--p-size-static-200);
    bottom: var(--p-size-static-150);
  }
  &__wrapper {
    display: inline-flex;
    position: relative;
    order: 3;
  }
}

anchor-input {
  &[disabled='true'] {
    .anchor-c-input__label,
    .anchor-c-input__help-msg {
      color: var(--s-color-text-disabled);
    }
  }
}

.anchor-c-number--w-big-spinners {
  margin: 0 var(--p-size-static-500);
  .anchor-c-input__label {
    margin-left: -var(--p-size-static-500);
  }
}
.hover {
  .anchor-c-input input {
    --border-color: var(--border-color-hover);
  }
}
.focused {
  .anchor-c-input input {
    --border-color: var(--border-color-focus);
  }
}
